import { getClientInfo, buildNotification, GenerateNotification } from 'smart-react';
import {
  postLoginLog,
  postLogin,
  getLookupByType,
  setUserTenant,
  getGlobalConfigById,
} from '../Services/LoginService';
import { listOrganization } from '../../../Admin/Organization/Services/OrganizationService';
import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  TOKEN_EXP,
  USER_DATA,
  INDEX_PAGE,
  ROLES,
  USER_PRODUCT_FEATURES,
  SKIP_TENANT_REQUEST,
} from '../../../../constants/applicationConstants';
import { getPersonalTenant } from '../../../../Utils/Auth/ValidatePermissions';
import { listProducts } from '../../../Products/Services/ProductService';
import { dexieCore } from '../../../../Utils/Storage/IndexDB/dexieDb';
import { GLOBAL_CONFIG } from '../../../../constants/applicationConstants';
import { encryptAndStore } from '../../../../Utils/Storage/localStorageUtil';
import { EVENTS_DATA_TYPES, NOTIFICATION_TYPES } from '../../../../constants/eventDataTypes';

/**
 * createLoginLog
 * @param response
 */
export const createLoginLog = async ({ dataItem, response, encodedData }) => {
  let clientInfo = getClientInfo();
  let reqDetails = {
    Request: encodedData.replace(/&password=[^&]+/, ''),
    Response: Object.fromEntries(
      Object.entries(response).filter(
        ([key]) => key !== 'access_token' && key !== 'refresh_token',
      ),
    ),
  };
  let appLog = {
    UserId: response?.error ? dataItem?.username : response?.userId,
    ActivityCode: 'LOGIN',
    RequestAllowed: response?.error ? false : true,
    RequestDisallowReason: response?.error
      ? JSON.stringify({
          StatusCode: JSON.parse(response?.error_description)?.StatusCode,
          StatusDesc: JSON.parse(response?.error_description)?.StatusDesc,
        })
      : '',
    Details: JSON.stringify(reqDetails),
    ...clientInfo,
  };
  postLoginLog({ data: appLog });
};
/**
 * getLookupData
 * @param response
 */

export const getLookupDataForMultipleTypes = ({ lookupTypes }) => {
  try {
    const lookupPromises = Object.keys(lookupTypes).map((key) => {
      const storageKey = `${lookupTypes[key]}`;

      // Check if data exists in localStorage
      const existingData = localStorage.getItem(storageKey);

      if (!existingData) {
        // Only call API if data doesn't exist in localStorage
        return getLookupByType({
          type: lookupTypes[key],
        }).then((response) => {
          encryptAndStore(storageKey, response?.Payload);
        });
      }

      // Return resolved promise if data already exists
      return Promise.resolve();
    });

    // Don't await - let promises run in background
    Promise.all(lookupPromises).catch((error) => {
      console.error('Error fetching lookup data:', error);
    });
  } catch (error) {
    console.error('Error fetching lookup data:', error);
  }
};

/**
 * get Role from Config
 * @param response
 */
export const getRolesFromConfig = async ({ roles }) => {
  try {
    const lookupPromises = Object.keys(roles).map((key) =>
      getGlobalConfigById({
        id: roles[key],
      }).then((response) => {
        encryptAndStore(`${roles[key]}`, response?.Payload);
      }),
    );

    await Promise.all(lookupPromises);
  } catch (error) {
    console.error('Error fetching lookup data:', error);
    throw error;
  }
};

/**
 * set Global Configuration in location storage
 * @param response
 */
export const setGlobalConfiguration = ({ configurations }) => {
  try {
    const lookupPromises = Object.keys(configurations).map((key) => {
      const storageKey = `${configurations[key]}`;

      // Check if data exists in localStorage
      const existingData = localStorage.getItem(storageKey);

      if (!existingData) {
        // Only call API if data doesn't exist in localStorage
        return getGlobalConfigById({
          id: configurations[key],
        }).then((response) => {
          encryptAndStore(storageKey, response?.Payload);
        });
      }

      // Return resolved promise if data already exists
      return Promise.resolve();
    });

    // Don't await - let promises run in background
    Promise.all(lookupPromises).catch((error) => {
      console.error('Error fetching lookup data:', error);
    });
  } catch (error) {
    console.error('Error fetching lookup data:', error);
  }
};

/**
 * set Product Features in location storage
 * @param response
 */
export const setProductFeatures = async ({ user }) => {
  try {
    const userData = { ...user, IsPrimary: false };
    const productsData = await listProducts({
      user: userData,
      tenantId: getPersonalTenant()?.TenantId,
      type: 'both',
    });
    encryptAndStore(`${USER_PRODUCT_FEATURES}`, productsData, false);
  } catch (error) {
    console.error('Error fetching product features data:', error);
    throw error;
  }
};

/**
 * Checks if user email domain validation should return true
 * Returns true if:
 * 1. User email domain doesn't exist in any organization's email_domain
 * 2. AND user email is not from common public domains (gmail.com, outlook.com, etc.)
 *
 * @param {string} userEmail - The user's email address
 * @param {string} page - page string
 * @returns {boolean} - True if validation conditions are met, false otherwise
 */
export const checkOrganizationRequest = async ({ user, page }) => {
  // Call both APIs in parallel
  const [userOrgs, responseConfig] = await Promise.all([
    listOrganization({ data: user }),
    getGlobalConfigById({
      id: GLOBAL_CONFIG?.PUBLIC_DOMAIN,
    }),
  ]);

  let userEmail = user?.userName;

  // List of common public email domains that should return false
  const domainConfig = responseConfig?.Payload;

  const publicDomains = domainConfig?.Value?.split(',')?.map((domain) =>
    domain.trim(),
  );

  // Extract domain from user email
  const userDomain = userEmail?.toLowerCase().split('@')[1];

  if (!userDomain) {
    return false; // Invalid email format
  }

  // Check if user email is from a public domain
  if (publicDomains?.includes(userDomain) || page === 'social') {
    return false;
  }
  // Check if user domain exists in any organization's email_domain
  const domainExistsInOrganizations = userOrgs?.Organizations?.some((org) => {
    if (!org.email_domain) {
      return false;
    }

    // Split email_domain by comma and check each domain
    const orgDomains = org?.email_domain
      ?.toLowerCase()
      ?.split(',')
      ?.map((domain) => domain.trim());
    return orgDomains?.includes(userDomain);
  });
  // Return true if domain doesn't exist in userOrgs AND it's not a public domain
  return !domainExistsInOrganizations;
};

/**
 * login
 * @param response
 */
export const login = async ({ dataItem, updateUser, page, onLoginError }) => {
  let response = await postLogin({ dataItem, createLoginLog });
  if (response && response?.error) {
    if (page && page !== 'login') {
      onLoginError({ response });
    }
  } else {
    const data = response;

    // First: Await all cookies to be set before proceeding
    await setAuthTokensCookie({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: data.expires_in,
      userData: data,
    });
    // Also store user data in localStorage
    encryptAndStore(USER_DATA, data);

    // After cookies are set, update user in context
    updateUser(data);

    if (page === 'social') {
      let configs = [
        {
          ConfigId: ACCESS_TOKEN,
          value: data.access_token,
        },
        {
          ConfigId: REFRESH_TOKEN,
          value: data.refresh_token,
        },
      ];
      await dexieCore.Configurations.bulkPut(configs);
    }

    // Start setUserTenant and the other operations in parallel
    const setUserTenantPromise = setUserTenant({ data });

    // Start these operations without waiting for setUserTenant
    const otherOperationsPromise = Promise.all([
      getRolesFromConfig({ roles: ROLES }),
    ]);

    // Wait for setUserTenant to complete first
    await setUserTenantPromise;

    // Then call setProductFeatures after setUserTenant completes
    await setProductFeatures({ user: data });

    // Make sure all operations are complete before redirecting
    await otherOperationsPromise;

    checkOrganizationRequest({ user: data, page })
      .then(async (isRequest) => {
        const skipTenant =
          typeof window !== 'undefined' && window.localStorage
            ? localStorage.getItem(SKIP_TENANT_REQUEST) === 'true'
            : false;
        if (isRequest && !skipTenant) {
          try {
            const filter = [
              {
                ColumnName: 'created_by',
                Op: 'contains',
                ColumnValue: data?.userName,
              },
            ];
            const orgsResp = await listOrganization({ limit: 1, offset: 0, filter });
            const found = orgsResp?.Organizations?.[0];
            if (found) {
              const isActive = found?.active === true || found?.is_approved === true;
              const target = INDEX_PAGE;
              const currentPath =
                typeof window !== 'undefined' && window.location
                  ? window.location.pathname
                  : '';
              if (!isActive) {
                try {
                  GenerateNotification(
                    buildNotification({
                      title: 'Request Pending',
                      description: 'Your organization request is already submitted and pending approval.',
                      style: 'info',
                    }),
                    NOTIFICATION_TYPES.APP,
                    EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
                  );
                } catch {}
              }
              if (currentPath !== target) {
                window.location.replace(target);
              }
            } else {
              window.location.replace('/tenant/request');
            }
          } catch (e) {
            // Fallback to tenant request on error
            window.location.replace('/tenant/request');
          }
        } else {
          const target = INDEX_PAGE;
          const currentPath =
            typeof window !== 'undefined' && window.location
              ? window.location.pathname
              : '';
          if (currentPath !== target) {
            window.location.replace(target);
          }
        }
      })
      .catch((error) => {
        //  window.location.replace(INDEX_PAGE);
      });
  }
  return {
    ...response,
    page,
  };
};

/**
 * setLoginSession - Sets up the login session with cookies and initializes auth data
 * @param {object} param0 - Contains data and updateUser function
 */
export const setLoginSession = async ({
  userData,
  updateUser,
  updateTenant,
  page,
  setAuthLoader,
  redirectTo,
  shouldRedirect = true,
}) => {
  // Guard against stale redirects:
  // On a hard refresh, session bootstrap can continue while the user navigates to another route.
  // If we blindly call window.location.replace at the end, it can "pull" the user back.
  // Capture the URL at start and only redirect if the user is still on that URL.
  const startUrl =
    typeof window !== 'undefined' && window.location
      ? `${window.location.pathname}${window.location.search || ''}`
      : '';

  // Also store user data in localStorage
  encryptAndStore(USER_DATA, userData);

  // After cookies are set, update user in context
  updateUser(userData);

  // Now: Start setUserTenant and the other operations in parallel
  const setUserTenantPromise = setUserTenant({ data: userData, updateTenant });

  // Start these operations without waiting for setUserTenant
  const otherOperationsPromise = Promise.all([
    getRolesFromConfig({ roles: ROLES }),
  ]);

  // Wait for setUserTenant to complete first
  await setUserTenantPromise;

  // Then call setProductFeatures after setUserTenant completes
  await setProductFeatures({ user: userData });

  // Make sure all operations are complete before redirecting
  await otherOperationsPromise;

  // Await checkOrganizationRequest before returning
  try {
    const isRequest = await checkOrganizationRequest({ user: userData, page });
    const skipTenant =
      typeof window !== 'undefined' && window.localStorage
        ? localStorage.getItem(SKIP_TENANT_REQUEST) === 'true'
        : false;
    if (isRequest && !skipTenant) {
      try {
        const filter = [
          {
            ColumnName: 'created_by',
            Op: 'contains',
            ColumnValue: userData?.userName,
          },
        ];
        const orgsResp = await listOrganization({ limit: 1, offset: 0, filter });
        const found = orgsResp?.Organizations?.[0];
        if (found) {
          const isActive = found?.active === true || found?.is_approved === true;
          const target = INDEX_PAGE;
          const currentPath =
            typeof window !== 'undefined' && window.location
              ? window.location.pathname
              : '';
          if (!isActive) {
            try {
              GenerateNotification(
                buildNotification({
                  title: 'Request Pending',
                  description: 'Your organization request is already submitted and pending approval.',
                  style: 'info',
                }),
                NOTIFICATION_TYPES.APP,
                EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
              );
            } catch {}
          }
          if (shouldRedirect) {
            const finalTarget = redirectTo || target;
            const currentUrl =
              typeof window !== 'undefined' && window.location
                ? `${window.location.pathname}${window.location.search || ''}`
                : '';

            const allowRedirectEvenIfNavigated =
              currentUrl.startsWith('/login') ||
              currentUrl.startsWith('/callback/') ||
              currentUrl.startsWith('/logout_callback');

            const shouldRedirectNow =
              allowRedirectEvenIfNavigated ||
              (startUrl && currentUrl === startUrl);

            if (shouldRedirectNow && currentUrl !== finalTarget) {
              window.location.replace(finalTarget);
            }
          }
        } else {
          const target = '/tenant/request';
          const currentPath =
            typeof window !== 'undefined' && window.location
              ? window.location.pathname
              : '';
          const currentUrl =
            typeof window !== 'undefined' && window.location
              ? `${window.location.pathname}${window.location.search || ''}`
              : '';
          const allowRedirectEvenIfNavigated =
            currentUrl.startsWith('/login') ||
            currentUrl.startsWith('/callback/') ||
            currentUrl.startsWith('/logout_callback');
          const shouldRedirectNow =
            allowRedirectEvenIfNavigated ||
            (startUrl && currentUrl === startUrl);
          if (shouldRedirectNow && currentPath !== target) {
            window.location.replace(target);
          }
        }
      } catch (e) {
        const target = '/tenant/request';
        const currentPath =
          typeof window !== 'undefined' && window.location
            ? window.location.pathname
            : '';

        const currentUrl =
          typeof window !== 'undefined' && window.location
            ? `${window.location.pathname}${window.location.search || ''}`
            : '';
        const allowRedirectEvenIfNavigated =
          currentUrl.startsWith('/login') ||
          currentUrl.startsWith('/callback/') ||
          currentUrl.startsWith('/logout_callback');
        const shouldRedirectNow =
          allowRedirectEvenIfNavigated ||
          (startUrl && currentUrl === startUrl);

        if (shouldRedirectNow && currentPath !== target) {
          window.location.replace(target);
        }
      }
    } else {
      const target = INDEX_PAGE;
      if (shouldRedirect) {
        const finalTarget = redirectTo || target;
        const currentUrl =
          typeof window !== 'undefined' && window.location
            ? `${window.location.pathname}${window.location.search || ''}`
            : '';

        const allowRedirectEvenIfNavigated =
          currentUrl.startsWith('/login') ||
          currentUrl.startsWith('/callback/') ||
          currentUrl.startsWith('/logout_callback');

        const shouldRedirectNow =
          allowRedirectEvenIfNavigated ||
          (startUrl && currentUrl === startUrl);

        if (shouldRedirectNow && currentUrl !== finalTarget) {
          window.location.replace(finalTarget);
        }
      }
    }
    if (typeof setAuthLoader === 'function') {
      setAuthLoader(false);
    }
  } catch (error) {
    console.error('Error checking organization request:', error);
    const target = INDEX_PAGE;
    if (shouldRedirect) {
      const finalTarget = redirectTo || target;
      const currentUrl =
        typeof window !== 'undefined' && window.location
          ? `${window.location.pathname}${window.location.search || ''}`
          : '';

      const allowRedirectEvenIfNavigated =
        currentUrl.startsWith('/login') ||
        currentUrl.startsWith('/callback/') ||
        currentUrl.startsWith('/logout_callback');

      const shouldRedirectNow =
        allowRedirectEvenIfNavigated ||
        (startUrl && currentUrl === startUrl);

      if (shouldRedirectNow && currentUrl !== finalTarget) {
        window.location.replace(finalTarget);
      }
    }
    if (typeof setAuthLoader === 'function') {
      setAuthLoader(false);
    }
  }

  return {
    ...userData,
  };
};

/**
 * setLoginSessionFromUserInfo - Sets up the login session using userinfo endpoint data
 * Used for cookie-based authentication where tokens are managed via HTTP-only cookies
 * @param {object} param0 - Contains userData, updateUser, and updateTenant functions
 */
export const setLoginSessionFromUserInfo = async ({
  userData,
  updateUser,
  updateTenant,
}) => {
  try {
    // Store user data in localStorage
    encryptAndStore(USER_DATA, userData);

    // Update user in context
    updateUser(userData);

    // // Start setUserTenant and other operations
    // const setUserTenantPromise = setUserTenant({ data: userData, updateTenant });

    // // Start these operations without waiting for setUserTenant
    // const otherOperationsPromise = Promise.all([
    //   getRolesFromConfig({ roles: ROLES }),
    // ]);

    // // Wait for setUserTenant to complete first
    // await setUserTenantPromise;

    // // Then call setProductFeatures after setUserTenant completes
    // await setProductFeatures({ user: userData });

    // // Make sure all operations are complete before redirecting
    // await otherOperationsPromise;

    // // Check for organization request
    // try {
    //   const isRequest = await checkOrganizationRequest({ user: userData });
    //   if (isRequest) {
    //     window.location.replace('/tenant/request');
    //   } else {
    //     window.location.replace(INDEX_PAGE);
    //   }
    // } catch (error) {
    //   console.error('Error checking organization request:', error);
    //   window.location.replace(INDEX_PAGE);
    // }

    return {
      ...userData,
    };
  } catch (error) {
    console.error('Error in setLoginSessionFromUserInfo:', error);
    throw error;
  }
};
